import { useState } from "react";
import { useLanguage } from "@/context/LanguageContext";
import { AlertTriangle, Bell, CheckCircle2, XCircle, Plus, Clock, Server, Activity, Shield, Zap, Settings, Trash2 } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Switch } from "@/components/ui/switch";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

const ACTIVE_ALERTS = [
  { id: "alert_001", severity: "critical", title: "QRNG-PROD-04 Offline", message: "Device is offline for scheduled maintenance. Entropy routing shifted to remaining 3 devices.", source: "Device Monitor", triggered: "2 hours ago", acknowledged: true },
  { id: "alert_002", severity: "warning", title: "Entropy Pool Below Threshold", message: "AWS entropy pool dropped to 65% at 12:00 UTC. Auto-recovery initiated - pool now at 87%.", source: "Entropy Proxy", triggered: "6 hours ago", acknowledged: false },
  { id: "alert_003", severity: "warning", title: "SP800-90B Chi-Square Warning", message: "QRNG-PROD-03 chi-square test is marginally passing. Temperature elevated to 38.7C.", source: "Health Monitor", triggered: "8 hours ago", acknowledged: true },
];

const RESOLVED_ALERTS = [
  { id: "alert_r01", severity: "critical", title: "VPN Link Saturation", resolved: "Feb 12, 2026", duration: "12 min" },
  { id: "alert_r02", severity: "warning", title: "Rate Limit Spike - org_crypto_004", resolved: "Feb 12, 2026", duration: "5 min" },
  { id: "alert_r03", severity: "warning", title: "DRBG Reseed Delay", resolved: "Feb 11, 2026", duration: "8 min" },
  { id: "alert_r04", severity: "critical", title: "API Gateway 5xx Errors", resolved: "Feb 10, 2026", duration: "3 min" },
];

const ALERT_RULES = [
  { id: "rule_001", name: "Device Offline", condition: "Device status = offline for > 1 min", channels: ["Email", "Slack", "PagerDuty"], enabled: true },
  { id: "rule_002", name: "Entropy Pool Low", condition: "Pool level < 70% for > 5 min", channels: ["Email", "Slack"], enabled: true },
  { id: "rule_003", name: "SP800-90B Failure", condition: "Any NIST test = Fail", channels: ["Email", "Slack", "PagerDuty", "SMS"], enabled: true },
  { id: "rule_004", name: "High API Error Rate", condition: "5xx rate > 1% for > 2 min", channels: ["Email", "Slack"], enabled: true },
  { id: "rule_005", name: "VPN/DX Latency High", condition: "DC-AWS latency > 50ms for > 3 min", channels: ["Email", "Slack"], enabled: true },
  { id: "rule_006", name: "Rate Limit Violations", condition: "> 100 rate limits/min for any tenant", channels: ["Email"], enabled: false },
  { id: "rule_007", name: "Certificate Expiry", condition: "Certificate expires within 30 days", channels: ["Email", "Slack"], enabled: true },
];

const severityIcon = (s) => s === 'critical' ? <XCircle className="w-4 h-4 text-red-500" /> : <AlertTriangle className="w-4 h-4 text-amber-500" />;
const severityBg = (s) => s === 'critical' ? 'border-l-red-500 bg-red-50/30' : 'border-l-amber-500 bg-amber-50/30';

export default function Alerts() {
  const { t } = useLanguage();
  const [showCreateRule, setShowCreateRule] = useState(false);

  return (
    <div data-testid="alerts-page">
      <div className="flex items-center justify-between mb-8">
        <div>
          <h1 className="text-2xl font-bold text-slate-900 tracking-tight" data-testid="alerts-title">Alerts & Notifications</h1>
          <p className="text-sm text-slate-500 mt-1">Monitor active alerts, configure rules, and manage notification channels</p>
        </div>
      </div>

      <Tabs defaultValue="active">
        <TabsList className="bg-white border border-slate-200 h-10 p-0.5 mb-6 w-auto" data-testid="alerts-tabs">
          <TabsTrigger value="active" className="text-xs px-4 gap-2 data-[state=active]:bg-slate-900 data-[state=active]:text-white" data-testid="tab-active-alerts">
            <AlertTriangle className="w-3.5 h-3.5" /> Active ({ACTIVE_ALERTS.length})
          </TabsTrigger>
          <TabsTrigger value="resolved" className="text-xs px-4 gap-2 data-[state=active]:bg-slate-900 data-[state=active]:text-white" data-testid="tab-resolved-alerts">
            <CheckCircle2 className="w-3.5 h-3.5" /> Resolved
          </TabsTrigger>
          <TabsTrigger value="rules" className="text-xs px-4 gap-2 data-[state=active]:bg-slate-900 data-[state=active]:text-white" data-testid="tab-alert-rules">
            <Settings className="w-3.5 h-3.5" /> Rules
          </TabsTrigger>
        </TabsList>

        {/* Active Alerts */}
        <TabsContent value="active">
          <div className="space-y-3" data-testid="active-alerts-list">
            {ACTIVE_ALERTS.map((alert, i) => (
              <div key={i} className={`bg-white border border-slate-200 border-l-4 ${severityBg(alert.severity)} p-5`} data-testid={`alert-card-${i}`}>
                <div className="flex items-start justify-between">
                  <div className="flex items-start gap-3">
                    {severityIcon(alert.severity)}
                    <div>
                      <div className="flex items-center gap-2 mb-1">
                        <h3 className="text-sm font-semibold text-slate-900">{alert.title}</h3>
                        <Badge variant="outline" className={`text-[9px] uppercase ${alert.severity === 'critical' ? 'bg-red-50 text-red-700 border-red-200' : 'bg-amber-50 text-amber-700 border-amber-200'}`}>{alert.severity}</Badge>
                      </div>
                      <p className="text-xs text-slate-600 leading-relaxed mb-2">{alert.message}</p>
                      <div className="flex items-center gap-4 text-[10px] text-slate-400">
                        <span>Source: {alert.source}</span>
                        <span>Triggered: {alert.triggered}</span>
                      </div>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    {alert.acknowledged ? (
                      <Badge className="bg-slate-100 text-slate-600 text-[9px] border-0">Acknowledged</Badge>
                    ) : (
                      <button className="text-xs text-cyan-600 hover:text-cyan-700 font-medium uppercase tracking-wider" data-testid={`ack-alert-${i}`}>Acknowledge</button>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </TabsContent>

        {/* Resolved Alerts */}
        <TabsContent value="resolved">
          <div className="bg-white border border-slate-200" data-testid="resolved-alerts-table">
          <div>
            <div className="grid grid-cols-[80px_1fr_140px_90px] bg-slate-50 border-b border-slate-200">
              {["Severity","Alert","Resolved","Duration"].map(h => (
                <div key={h} className="px-4 py-2.5 text-xs font-semibold text-slate-500 uppercase tracking-wide">{h}</div>
              ))}
            </div>
            {RESOLVED_ALERTS.map((alert, i) => (
              <div key={i} className="grid grid-cols-[80px_1fr_140px_90px] border-b border-slate-100 hover:bg-slate-50/50 transition-colors" data-testid={`resolved-alert-row-${i}`}>
                <div className="px-4 py-3 flex items-center">
                  <Badge variant="outline" className={`text-[9px] uppercase ${alert.severity === 'critical' ? 'bg-red-50 text-red-700 border-red-200' : 'bg-amber-50 text-amber-700 border-amber-200'}`}>{alert.severity}</Badge>
                </div>
                <div className="px-4 py-3 text-sm text-slate-900 flex items-center">{alert.title}</div>
                <div className="px-4 py-3 text-xs text-slate-500 flex items-center">{alert.resolved}</div>
                <div className="px-4 py-3 text-xs font-mono text-slate-600 flex items-center">{alert.duration}</div>
              </div>
            ))}
          </div>
          </div>
        </TabsContent>

        {/* Alert Rules */}
        <TabsContent value="rules">
          <div className="flex items-center justify-between mb-4">
            <p className="text-sm text-slate-500">Configure when and how you receive alerts</p>
            <button onClick={() => setShowCreateRule(true)} className="inline-flex items-center gap-2 bg-slate-900 text-white hover:bg-slate-800 transition-colors text-xs font-bold uppercase tracking-widest px-4 py-2" data-testid="create-alert-rule-btn">
              <Plus className="w-3.5 h-3.5" /> New Rule
            </button>
          </div>
          <div className="space-y-2" data-testid="alert-rules-list">
            {ALERT_RULES.map((rule, i) => (
              <div key={i} className="bg-white border border-slate-200 p-4 flex items-center justify-between" data-testid={`alert-rule-${i}`}>
                <div className="flex items-center gap-4 flex-1">
                  <Switch checked={rule.enabled} data-testid={`rule-toggle-${i}`} />
                  <div>
                    <div className="text-sm font-medium text-slate-900">{rule.name}</div>
                    <div className="text-xs text-slate-500 mt-0.5 font-mono">{rule.condition}</div>
                  </div>
                </div>
                <div className="flex items-center gap-3">
                  <div className="flex gap-1">
                    {rule.channels.map((ch, j) => (
                      <Badge key={j} variant="outline" className="text-[9px] text-slate-500 border-slate-200">{ch}</Badge>
                    ))}
                  </div>
                  <button className="p-1 text-slate-400 hover:text-red-500" data-testid={`delete-rule-${i}`}>
                    <Trash2 className="w-3.5 h-3.5" />
                  </button>
                </div>
              </div>
            ))}
          </div>
        </TabsContent>
      </Tabs>

      {/* Create Rule Dialog */}
      <Dialog open={showCreateRule} onOpenChange={setShowCreateRule}>
        <DialogContent className="sm:max-w-md" data-testid="create-rule-dialog">
          <DialogHeader>
            <DialogTitle className="text-lg font-bold tracking-tight">Create Alert Rule</DialogTitle>
            <DialogDescription className="text-sm text-slate-500">Define conditions and notification channels for a new alert rule.</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div>
              <Label className="text-xs uppercase tracking-wider text-slate-500 font-medium">Rule Name</Label>
              <Input placeholder="e.g., High Latency Alert" className="mt-1.5 h-10" data-testid="rule-name-input" />
            </div>
            <div>
              <Label className="text-xs uppercase tracking-wider text-slate-500 font-medium">Metric</Label>
              <Select>
                <SelectTrigger className="mt-1.5 h-10" data-testid="rule-metric-select"><SelectValue placeholder="Select metric" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="device_status">Device Status</SelectItem>
                  <SelectItem value="entropy_pool">Entropy Pool Level</SelectItem>
                  <SelectItem value="api_latency">API Latency</SelectItem>
                  <SelectItem value="error_rate">Error Rate</SelectItem>
                  <SelectItem value="sp90b_test">SP800-90B Test Result</SelectItem>
                  <SelectItem value="vpn_latency">VPN/DX Latency</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs uppercase tracking-wider text-slate-500 font-medium">Severity</Label>
              <Select>
                <SelectTrigger className="mt-1.5 h-10" data-testid="rule-severity-select"><SelectValue placeholder="Select severity" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="critical">Critical</SelectItem>
                  <SelectItem value="warning">Warning</SelectItem>
                  <SelectItem value="info">Info</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter className="gap-2">
            <button onClick={() => setShowCreateRule(false)} className="px-4 py-2 text-sm text-slate-600 border border-slate-200 hover:bg-slate-50 transition-colors" data-testid="cancel-rule-btn">Cancel</button>
            <button onClick={() => setShowCreateRule(false)} className="px-4 py-2 text-sm bg-slate-900 text-white hover:bg-slate-800 transition-colors uppercase tracking-wider font-bold" data-testid="save-rule-btn">Create Rule</button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
